/** React */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/** MUI icons */
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** MUI */
import { styled } from "@mui/material/styles";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/** Component styles */
const PREFIX = "AccountantSummary";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    summaryLabels: `${PREFIX}-summaryLabels`,
    summaryDetails: `${PREFIX}-summaryDetails`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 30px 30px !important",
    },

    [`& .${classes.summaryLabels}`]: {
        display: "flex",
        justifyContent: "space-between",
        padding: "24px 0px 8px",
        borderBottom: "1px solid #cccc",
    },

    [`& .${classes.summaryDetails}`]: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "16px",
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const AccountantSummary = (props) => {
    /** Properties */
    const { valuesAccRecu, valuesAccCleanUp, valuesAccCatchUp, valuesAccOnboard, valuesAccConsul } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [expanded, setExpanded] = useState(true);

    /** Component functions */
    const handleChangeSumaryAccordion = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    return (
        <StyledContainer>
            <Card
                elevation={0}
                sx={{
                    mt: 3,
                    borderRadius: 8,
                }}
            >
                <CardContent sx={{ pt: 1 }}>
                    <Accordion
                        expanded={expanded}
                        onChange={handleChangeSumaryAccordion}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            aria-controls="panel-content"
                            id="panel-header"
                            sx={{
                                borderBottom: "2px solid #ff5968",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Typography
                                        variant="h2"
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t(`invoicing.accountant`)}
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ pr: 1 }}>
                                    {!valuesAccRecu.payAnnually && (
                                        <Typography variant="h3" sx={{ pb: 0.5 }}>
                                            {`$${parseFloat(
                                                !valuesAccRecu.chip
                                                    ? 0
                                                    : valuesAccRecu.recuProjectTotal +
                                                          valuesAccRecu.recuHours *
                                                              valuesAccRecu.recuHourRate *
                                                              (1 - valuesAccRecu.recuDiscount / 100)
                                            ).toLocaleString(todos.amountFormat, {
                                                maximumFractionDigits: 2,
                                            })}/${t(`proposals.month`)}`}
                                        </Typography>
                                    )}
                                    <Typography variant="h3">
                                        {`$${parseFloat(
                                            (!valuesAccOnboard.chip ? 0 : valuesAccOnboard.onboardProjectTotal) +
                                                (!valuesAccCleanUp.chip
                                                    ? 0
                                                    : valuesAccCleanUp.projectTotal +
                                                      valuesAccCleanUp.totalHours *
                                                          valuesAccCleanUp.hourRate *
                                                          (1 - valuesAccCleanUp.discount / 100)) +
                                                (!valuesAccCatchUp.chip
                                                    ? 0
                                                    : valuesAccCatchUp.projectTotal +
                                                      valuesAccCatchUp.totalHours *
                                                          valuesAccCatchUp.hourRate *
                                                          (1 - valuesAccCatchUp.discount / 100)) +
                                                (!valuesAccRecu.chip || !valuesAccRecu.payAnnually
                                                    ? 0
                                                    : valuesAccRecu.recuProjectTotal +
                                                      valuesAccRecu.recuHours *
                                                          valuesAccRecu.recuHourRate *
                                                          (1 - valuesAccRecu.recuDiscount / 100)) +
                                                (!valuesAccConsul.chip ? 0 : valuesAccConsul.consulProjectTotal)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}/${t(`proposals.oneTime`)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            {valuesAccOnboard.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesAccOnboard.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.onboarding`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesAccOnboard.onboardProjectTotal
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    backgroundColor: valuesAccOnboard.isError? "#ff5968": "#2f3190",
                                                    //backgroundColor: "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesAccCleanUp.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesAccCleanUp.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.cleanUp`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesAccCleanUp.projectTotal +
                                                                valuesAccCleanUp.totalHours *
                                                                    valuesAccCleanUp.hourRate *
                                                                    (1 - valuesAccCleanUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesAccCleanUp.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesAccCleanUp.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesAccCleanUp.months} ${t(
                                                        "proposals.months"
                                                    )})`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesAccCleanUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesAccCleanUp.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                        {valuesAccCleanUp.cleanUpChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesAccCleanUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)} (${
                                                            valuesAccCleanUp.totalHours
                                                        } hrs)`}
                                                    </Typography>
                                                    <Typography variant="body2" align="center" sx={{color: valuesAccCleanUp.isError? "#ff5968": "primary"}}>
                                                        {`$${parseFloat(
                                                            valuesAccCleanUp.totalHours *
                                                                valuesAccCleanUp.hourRate *
                                                                (1 - valuesAccCleanUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesAccCatchUp.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesAccCatchUp.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.catchUp`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesAccCatchUp.projectTotal +
                                                                valuesAccCatchUp.totalHours *
                                                                    valuesAccCatchUp.hourRate *
                                                                    (1 - valuesAccCatchUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesAccCatchUp.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesAccCatchUp.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesAccCatchUp.months} ${t(
                                                        "proposals.months"
                                                    )})`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesAccCatchUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesAccCatchUp.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                        {valuesAccCatchUp.catchUpChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesAccCatchUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)} (${
                                                            valuesAccCatchUp.totalHours
                                                        } hrs)`}
                                                    </Typography>
                                                    <Typography variant="body2" align="center"  sx={{color: valuesAccCatchUp.isError? "#ff5968": "primary"}} >
                                                        {`$${parseFloat(
                                                            valuesAccCatchUp.totalHours *
                                                                valuesAccCatchUp.hourRate *
                                                                (1 - valuesAccCatchUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesAccRecu.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesAccRecu.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {`${t(`invoicing.recurring`)}${valuesAccRecu.payAnnually ? "**" : ""}`}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesAccRecu.recuProjectTotal +
                                                                valuesAccRecu.recuHours *
                                                                    valuesAccRecu.recuHourRate *
                                                                    (1 - valuesAccRecu.recuDiscount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${
                                                            valuesAccRecu.payAnnually
                                                                ? t(`proposals.oneTime`)
                                                                : t(`proposals.month`)
                                                        }`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesAccRecu.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: valuesAccRecu.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t(`proposals.basicService`)}
                                                </Typography>
                                                <Typography variant="body2" align="center" sx={{color: valuesAccRecu.isError? "#ff5968": "primary"}}>
                                                    {`$${parseFloat(valuesAccRecu.recuProjectTotal).toLocaleString(
                                                        todos.amountFormat,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}/${
                                                        valuesAccRecu.payAnnually
                                                            ? t(`proposals.oneTime`)
                                                            : t(`proposals.month`)
                                                    }`}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {valuesAccRecu.recuChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesAccRecu.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)} (${valuesAccRecu.recuHours} hrs)*`}
                                                    </Typography>
                                                    <Typography variant="body2" align="center" sx={{color: valuesAccRecu.isError? "#ff5968": "primary"}}>
                                                        {`$${parseFloat(
                                                            valuesAccRecu.recuHourRate *
                                                                valuesAccRecu.recuHours *
                                                                (1 - valuesAccRecu.recuDiscount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${
                                                            valuesAccRecu.recuServFreq === 1
                                                                ? t("proposals.month")
                                                                : t("proposals.oneTime")
                                                        }`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesAccConsul.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesAccConsul.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.consulting`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesAccConsul.consulProjectTotal
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesAccConsul.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesAccConsul.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t(`proposals.query`)} (${valuesAccConsul.consulHours} hrs)`}
                                                </Typography>
                                                <Typography variant="body2" align="center" sx={{color: valuesAccConsul.isError? "#ff5968": "primary"}}>
                                                    {`$${parseFloat(valuesAccConsul.consulProjectTotal).toLocaleString(
                                                        todos.amountFormat,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}/${t(`proposals.oneTime`)}`}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </StyledContainer>
    );
};
